import { acceptHMRUpdate, defineStore } from 'pinia'
import { ref } from 'vue'
import type { HttpMethod } from '@cplug/frontend-core/models'
import {
  EMPTY_FORBIDDEN_ACCESS_CONTEXT,
  normalizeMissingPermissions,
  readForbiddenAccessFromSessionStorage,
  removeForbiddenAccessFromSessionStorage,
  writeForbiddenAccessToSessionStorage,
  type ForbiddenAccessContext
} from '~/shared/models/ForbiddenAccess'

type ForbiddenAccessPatch = Pick<ForbiddenAccessContext, 'blockedFrom' | 'blockedApiUrl' | 'blockedApiMethod'>

const useForbiddenAccessStore = defineStore('shared/forbidden-access', () => {
  const missingPermissions = ref<string[]>([])
  const blockedFrom = ref<string | null>(null)
  const blockedApiUrl = ref<string | null>(null)
  const blockedApiMethod = ref<HttpMethod | null>(null)
  const skipNextRestore = ref(false)

  function applyContext (context: ForbiddenAccessContext): void {
    missingPermissions.value = normalizeMissingPermissions(context.missingPermissions)
    blockedFrom.value = context.blockedFrom
    blockedApiUrl.value = context.blockedApiUrl
    blockedApiMethod.value = context.blockedApiMethod
  }

  function markRedirectArrival (): void {
    skipNextRestore.value = true
  }

  function consumeSkipNextRestore (): boolean {
    if (!skipNextRestore.value) {
      return false
    }

    skipNextRestore.value = false
    return true
  }

  function setForbiddenAccess (context: ForbiddenAccessContext): void {
    const normalizedContext: ForbiddenAccessContext = {
      ...context,
      missingPermissions: normalizeMissingPermissions(context.missingPermissions)
    }

    applyContext(normalizedContext)
    writeForbiddenAccessToSessionStorage(normalizedContext)
  }

  function addMissingPermissions (slugs: string[], patch: ForbiddenAccessPatch): void {
    const persisted = readForbiddenAccessFromSessionStorage()
    const previousBlockedFrom = persisted.blockedFrom ?? blockedFrom.value
    const nextBlockedFrom = patch.blockedFrom ?? previousBlockedFrom

    const isSameBlockingPage = previousBlockedFrom != null &&
      nextBlockedFrom != null &&
      previousBlockedFrom === nextBlockedFrom

    const basePermissions = isSameBlockingPage
      ? normalizeMissingPermissions([
        ...persisted.missingPermissions,
        ...missingPermissions.value
      ])
      : []

    const mergedPermissions = normalizeMissingPermissions([
      ...basePermissions,
      ...slugs
    ])

    const nextContext: ForbiddenAccessContext = {
      missingPermissions: mergedPermissions,
      blockedFrom: nextBlockedFrom,
      blockedApiUrl: isSameBlockingPage
        ? (patch.blockedApiUrl ?? persisted.blockedApiUrl ?? blockedApiUrl.value)
        : (patch.blockedApiUrl ?? null),
      blockedApiMethod: isSameBlockingPage
        ? (patch.blockedApiMethod ?? persisted.blockedApiMethod ?? blockedApiMethod.value)
        : (patch.blockedApiMethod ?? null)
    }

    applyContext(nextContext)
    writeForbiddenAccessToSessionStorage(nextContext)
  }

  function hydrateFromSession (): void {
    applyContext(readForbiddenAccessFromSessionStorage())
  }

  function clearForbiddenAccess (): void {
    applyContext({ ...EMPTY_FORBIDDEN_ACCESS_CONTEXT })
    removeForbiddenAccessFromSessionStorage()
  }

  return {
    missingPermissions,
    blockedFrom,
    blockedApiUrl,
    blockedApiMethod,
    setForbiddenAccess,
    addMissingPermissions,
    hydrateFromSession,
    clearForbiddenAccess,
    markRedirectArrival,
    consumeSkipNextRestore
  }
})

export default useForbiddenAccessStore

/* c8 ignore next 3 */
if ((import.meta as any).hot) {
  (import.meta as any).hot.accept(acceptHMRUpdate(useForbiddenAccessStore, (import.meta as any).hot))
}
